<template>
  <sub-section :title="title" helpTitle="Your pension pots" :showHelp="showHelp" :helpText="HelpText.PensionPots()">
    <slot name="message"></slot>
    <div v-show="hasPensionPots" class="table-responsive mb-2">
      <table class="table table-striped align-middle">
        <thead>
          <tr>
            <th v-if="selectionEnabled">{{ selectedColumnTitle }}</th>
            <th>Provider</th>
            <th class="numeric">Current value</th>
            <th class="numeric">
              Personal contribution
              <help v-if="showCurrentContributionHelp" title="Pension contributions"
                :text="HelpText.pensionContributions()" />
            </th>
            <th class="numeric">
              Employer contribution
            </th>
            <th v-if="showDetails" class="numeric">
              Estimated value at retirement
              <help title="Estimated value at retirement" :text="HelpText.PensionPotsEstValAtRetirement()" />
            </th>
            <th v-if="showEditButtons"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="pensionPot in getPensionPots" v-bind:key="pensionPot.name">
            <td v-if="selectionEnabled">
              <div class="form-check form-switch">
                <input
                  type="checkbox" class="form-check-input todays-money-chk" role="switch"
                  :id="pensionPot.id.toString()" :value="pensionPot" v-model="selectedPensionPots"
                />
              </div>
            </td>
            <td>{{ pensionPot.name }}</td>
            <td class="numeric">
              {{ formatter.formatCurrency(pensionPot.currentValue) }}
            </td>
            <td class="numeric">
              {{ formatter.formatCurrency(pensionPot.personalMonthlyContribution) }}
            </td>
            <td class="numeric">
              {{ formatter.formatCurrency(pensionPot.employerMonthlyContribution) }}
            </td>
            <td v-if="showDetails" class="numeric">
              {{ formatter.formatCurrency(pensionPot.estimatedValue) }}
            </td>
            <td v-if="showEditButtons">
              <three-dot-menu :id="pensionPot.id" :permissions="getPensionPermissions(pensionPot)" @viewClicked="onView" @editClicked="onEdit" @deleteClicked="onDelete" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-show="!hasPensionPots">
        <empty-asset name="pension pots" />
    </div>

    <div v-if="showAddButton" class="row">
      <add-asset name="pot" longName="another pension pot manually" instruction="Click Add (+) to add more manually." @addClicked="onAdd()" />
    </div>

    <div v-if="showAdvice && showLumpSumInfo" class="row mt-3">
      <div class="col d-flex align-items-center">
        <p class="text-muted align-middle mb-0">
          {{ lumpSumInfo }}
        </p>
      </div>
    </div>

    <div v-if="showAdvice && showGuaranteedIncomeInfo" class="mt-3">
      <div class="col border-top pt-3">
        <p class="text-muted">
          {{ guaranteedIncomeText }}
          <help title="Guaranteed income" :text="HelpText.guaranteedIncome(annuityType)" />
        </p>
        <div class="d-grid gap-2 d-sm-block">
          <button type="button" class="btn btn-dark btn-sm me-sm-2" @click="onUnsubscribeGuaranteedIncomeClicked($event)">Remove</button>
          <button type="button" class="btn btn-primary btn-sm" @click="emit('guaranteed-income-change-required')">Change</button>
        </div>
      </div>
    </div>

    <template v-if="showAdvice && canLinkLiveValues">
      <div v-if="!isLinkedToLiveValues" class="alert alert-info mb-2 mt-3">
        <i class="bi bi-database-add me-2"></i>
        Add live pots via our
        <a @click="onTransferToPensionLab($event)" href="" class="alert-link skin-link me-1">live dashboard setup</a>
        <help title="Live pension pots" :text="HelpText.livePensionPots()" />
      </div>
      <div v-else class="alert alert-secondary mb-2 mt-3">
        <i class="bi bi-info-circle-fill"></i>
        {{ linkMoreLiveFundsAdviceText }}
        <a @click="onTransferToPensionLab($event)" href="" class="alert-link">here</a>
      </div>
    </template>
  </sub-section>

  <modal :title="modalTitle" ref="potModal" idKey="pensionPot">
    <pension-pot-editor ref="potEditor" @edit-finished="onEditFinished" :pension-pot="pensionPotToEdit" />
  </modal>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { PensionPot } from "@/guiide/ts/pensionPot";
import Modal from "@/guiide/components/common/Modal.vue";
import PensionPotEditor from "@/guiide/components/assets/PensionPotEditor.vue";
import SubSection from "@/guiide/components/common/SubSection.vue";
import AddAsset from "@/guiide/components/assets/AddAsset.vue";
import EmptyAsset from "@/guiide/components/assets/EmptyAsset.vue";
import ObjectBuilder from "@/guiide/ts/objectBuilder";
import PermissionService from "@/guiide/ts/permissionService";
import Constants from "@/guiide/ts/constants";
import HelpText from "@/guiide/ts/helpText";
import Help from "@/guiide/components/common/Help.vue";
import ThreeDotMenu from "@/guiide/components/common/ThreeDotMenu.vue";
import { useStore } from "vuex";
import { formatter } from "../../../shared/ts/formatter";
import { app } from "@/guiide/ts/app";
import Monitoring from "@/guiide/ts/monitoring";
import { GuaranteedIncome } from "@/guiide/ts/guaranteedIncome";
import View from "@/guiide/ts/view";
import AnnuityType from "@/guiide/ts/annuityType";

const props = defineProps({
    showDetails: { type: Boolean, required: false, default: true },
    showEditButtons: { type: Boolean, required: false, default: true },
    showAddButton: { type: Boolean, required: false, default: true },
    showAdvice: { type: Boolean, required: false, default: true },
    showHelp: { type: Boolean, required: false, default: true },
    title: { type: String, required: false, default: "Your Pension Pots"},
    selectionEnabled: { type: Boolean, required: false, default: false },
    selectedColumnTitle: { type: String, required: false, default: "Included" }
});

const emit = defineEmits(['guaranteed-income-change-required']);

defineExpose({ getSelectedPensionPots, setSelectedPensionPots });

const store = useStore();

const potModal = ref(null);
const potEditor = ref(null);
const modalTitle = ref("");
const pensionPotToEdit = ref(ObjectBuilder.createPensionPot(Constants.NewId, "", 0, 0, false, 0, 0, true, true, true));
const selectedPensionPots = ref([]);

onMounted(() => {
  selectedPensionPots.value = getPensionPots.value;
});

const hasPensionPots = computed<boolean>(() => {
  return store.state.retiree?.pensionPots.pots.length > 0;
});

const getPensionPots = computed<Array<PensionPot>>(() => {
  return (
    store.state.retiree?.pensionPots.pots ?? new Array<PensionPot>()
  );
});

const canLinkLiveValues = computed<boolean>(() => {
  if (store.state.view == View.Haves) {
    return store.state.configuration.canLinkLiveValuesFromHaves;
  } else if (
    store.state.view == View.Dashboard ||
    store.state.view == View.GuiidePlus
  ) {
    return store.state.configuration.canLinkLiveValuesFromDashboard;
  } else {
    return false;
  }
});

const isLinkedToLiveValues = computed<boolean>(() => {
  return store.state.retiree?.isLinkedToLiveValues ?? false;
});

const linkMoreLiveFundsAdviceText = computed<string>(() => {
  return store.state.configuration.liveValuesLinkMoreFundsAdviceText;
});

const showCurrentContributionHelp = computed<boolean>(() => {
  return store.state.configuration
    .dashboardShowCurrentContributionHelp;
});

const showLumpSumInfo = computed<boolean>(() => {
  if (store.state.retiree == null) {
    return false;
  }
  if (store.state.retiree.alreadyDrawingPension) {
    return false;
  }
  return (
    store.state.retiree.requirements.takingTaxFreeAsLumpSum && hasPensionPots.value
  );
});

const lumpSumInfo = computed<string>(() => {
  if (!showLumpSumInfo.value) {
    return "";
  }

  const lumpSumAmount = store.state.retiree.pensionPots.lumpSumValueAtRetirement;
  let lumpSumAmountFormatted: string;
  let reductionStatement: string;

  if (lumpSumAmount < Constants.TaxFreeCashLimit) {
    lumpSumAmountFormatted = formatter.formatCurrency(lumpSumAmount);
    reductionStatement = `The total estimated figure at retirement will immediately be reduced
      by ${lumpSumAmountFormatted} as you have chosen to take lump sum tax free cash.`;
  } else {
    lumpSumAmountFormatted = formatter.formatCurrency(Constants.TaxFreeCashLimit);
    reductionStatement = `The total estimated figure at retirement will immediately be reduced
      by ${lumpSumAmountFormatted} (the current maximum tax free cash possible) as you have 
      chosen to take lump sum tax free cash.`;
  }

  return `${reductionStatement} We assume you will spend this, but you can add this
      back into other cash below at your retirement date if not.`
});

const showGuaranteedIncomeInfo = computed<boolean>(() => {
  if (!store.state.configuration.guaranteedIncomeEnabled) {
    return false;
  }
  return store.state.retiree?.guaranteedIncome.subscribed ?? false;
});

const annuityType = computed<AnnuityType>(() => {
  return store.state.configuration.annuityType;
});

const guaranteedIncomeText = computed<string>(() => {
  if (!showGuaranteedIncomeInfo.value) {
    return "";
  }

  const guaranteedIncome = store.state.retiree.guaranteedIncome as GuaranteedIncome;
  const allocationAmount = formatter.formatCurrency(guaranteedIncome.allocationAmount);
  const annualIncome = formatter.formatCurrency(guaranteedIncome.annualIncome);
  const subscriptionDateFormatted = formatter.monthAndYearDate(store.state.retiree.guaranteedIncome.subscriptionDate);

  let remaining = "";
  if (store.state.retiree.requirements.takingTaxFreeAsLumpSum) {
    remaining = "remaining ";
  }

  const pot = getPensionPots.value.length == 1 ? "pot" : "pots";

  switch (annuityType.value) {
    case AnnuityType.GuaranteedIncome:
      return (
        `You have chosen to see how ${guaranteedIncome.allocation}% (${allocationAmount}) of your ${remaining}non protected pension ${pot} could be used to buy a guaranteed` +
        ` income paid into your ${pot} from ${subscriptionDateFormatted}. On the illustrative example, it would mean a guaranteed income of ${annualIncome}.`
      );
    case AnnuityType.Traditional:
      return `You have chosen to use ${guaranteedIncome.allocation}% (${allocationAmount}) of your ${remaining} non protected pension ${pot} to model an estimated guaranteed` +
        ` income of ${annualIncome}, which has been added to other income below.`

    default:
      throw new Error("Unknown annuity type.");
  }
});

function getPensionPermissions(pensionPot: PensionPot) {
  return PermissionService.getPermissionSet(pensionPot);
}

function onEditFinished() {
  potModal.value.closeModal();
}

function onAdd() {
  modalTitle.value = "Add pension pot";
  const pensionPot = ObjectBuilder.createPensionPot(Constants.NewId, "", 0, 0, false, 0, 0, true, true, true);
  pensionPotToEdit.value = pensionPot;
  potModal.value.showModal();
}

function onEdit(id: number) {
  const pensionPot = getPensionPots.value.filter((p) => p.id == id)[0];
  showPensionEditor("Edit pension pot", pensionPot);
}

function onView(id: number) {
  const pensionPot = getPensionPots.value.filter((p) => p.id == id)[0];
  showPensionEditor("View pension pot", pensionPot);
}

function showPensionEditor(title: string, pensionPot: PensionPot) {
  modalTitle.value = title;
  pensionPotToEdit.value = pensionPot;
  potModal.value.showModal();
}

async function onDelete(id: number) {
  await app.deletePensionPot(id);
}

async function onTransferToPensionLab(event: any) {
  event.preventDefault();
  Monitoring.pensionLabDashboardEvent(store.state.view);
  await app.transferToPensionLab();
}

async function onUnsubscribeGuaranteedIncomeClicked(event: any) {
  event.preventDefault();
  await app.deleteGuaranteedIncome();
}

function getSelectedPensionPots(): Array<PensionPot> {
  return selectedPensionPots.value;
}

function setSelectedPensionPots(pensionPots: Array<PensionPot>) {
  for (const pensionPot of pensionPots) {
    selectedPensionPots.value.push(pensionPot);
  }
}
</script>

<style scoped>
th.numeric {
  text-align: right;
}

td.numeric {
  text-align: right;
}

@media (min-width: 768px) {
  .table-responsive {
    overflow: visible;
  }
}
</style>
